import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import {
 createEquipment,
 updateEquipment,
 getEquipment,
} from "../../../services/equipmentService";

const CATEGORIES = [
 "Heavy Machinery",
 "Light Machinery",
 "Vehicles",
 "Power Tools",
 "Hand Tools",
 "Measuring Instruments",
 "Safety Equipment",
 "Other",
];

const STATUSES = [
 "Available",
 "Deployed",
 "Under Maintenance",
 "Idle",
 "Retired",
 "In Transit",
];

const MAINTENANCE_STATUSES = ["Good", "Due Soon", "Overdue", "Under Repair", "N/A"];

const initialForm = {
 name: "",
 category: "Other",
 type: "",
 make: "",
 model: "",
 serialNumber: "",
 yearOfManufacture: "",
 location: "",
 status: "Available",
 maintenanceStatus: "Good",
 nextMaintenanceDate: "",
 lastMaintenanceDate: "",
 project: "",
 workOrder: "",
 operator: "",
 remarks: "",
};

const EquipmentForm = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = mode === "edit";

 const [form, setForm] = useState(initialForm);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(false);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);

 /* Load reference data */
 useEffect(() => {
 const load = async () => {
 try {
 const [pRes, uRes] = await Promise.all([
 api.get("/projects", { params: { limit: 200 } }),
 api.get("/users", { params: { limit: 200 } }),
 ]);
 setProjects(pRes.data?.data || pRes.data?.projects || []);
 setUsers(uRes.data?.data || uRes.data?.users || []);
 } catch (_) {}
 };
 load();
 }, []);

 /* Load work orders when project changes */
 useEffect(() => {
    const params = form.project ? { project: form.project, limit: 1000 } : { limit: 1000 };
    api
      .get("/work-orders", { params })
      .then((res) => setWorkOrders(res.data?.data || res.data?.workOrders || []))
      .catch(() => {});
  }, [form.project]);

 /* Load equipment when editing */
 useEffect(() => {
 if (!isEdit || !id) return;
 setFetching(true);
 getEquipment(id)
 .then((res) => {
 const e = res.data?.data;
 if (!e) return;
 setForm({
 name: e.name || "",
 category: e.category || "Other",
 type: e.type || "",
 make: e.make || "",
 model: e.model || "",
 serialNumber: e.serialNumber || "",
 yearOfManufacture: e.yearOfManufacture || "",
 location: e.location || "",
 status: e.status || "Available",
 maintenanceStatus: e.maintenanceStatus || "Good",
 nextMaintenanceDate: e.nextMaintenanceDate
 ? e.nextMaintenanceDate.slice(0, 10)
 : "",
 lastMaintenanceDate: e.lastMaintenanceDate
 ? e.lastMaintenanceDate.slice(0, 10)
 : "",
 project: e.project?._id || e.project || "",
 workOrder: e.workOrder?._id || e.workOrder || "",
 operator: e.operator?._id || e.operator || "",
 remarks: e.remarks || "",
 });
 })
 .catch(() => toast.error("Failed to load equipment"))
 .finally(() => setFetching(false));
 }, [isEdit, id]);

   const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project") {
        if (value && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(value)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (value) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(value));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.name.trim()) {
 toast.error("Equipment name is required");
 return;
 }

 const payload = { ...form };
 if (!payload.project) delete payload.project;
 if (!payload.workOrder) delete payload.workOrder;
 if (!payload.operator) delete payload.operator;
 if (!payload.nextMaintenanceDate) delete payload.nextMaintenanceDate;
 if (!payload.lastMaintenanceDate) delete payload.lastMaintenanceDate;
 if (!payload.yearOfManufacture) delete payload.yearOfManufacture;

 setLoading(true);
 try {
 if (isEdit) {
 await updateEquipment(id, payload);
 toast.success("Equipment updated");
 } else {
 await createEquipment(payload);
 toast.success("Equipment created");
 }
 navigate("../equipment");
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to save equipment");
 } finally {
 setLoading(false);
 }
 };

 if (fetching) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 return (
 <>
 <PageTitle
 activeMenu={isEdit ? "Edit Equipment" : "Add Equipment"}
 motherMenu="Equipment"
 />

 <Card className="shadow-sm">
 <Card.Header>
 <h5 className="mb-0">
 <i className="fas fa-cogs me-2 text-primary" />
 {isEdit ? "Edit Equipment" : "Add New Equipment"}
 </h5>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {/* Basic Info */}
 <h6 className="text-muted mb-3 text-uppercase" style={{ fontSize: "0.78rem", letterSpacing: 1 }}>
 Basic Information
 </h6>
 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Equipment Name <span className="text-danger">*</span></Form.Label>
 <Form.Control
 name="name"
 value={form.name}
 onChange={handleChange}
 placeholder="e.g. Excavator JCB 3CX"
 required
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Category</Form.Label>
 <Form.Select name="category" value={form.category} onChange={handleChange}>
 {CATEGORIES.map((c) => (
 <option key={c} value={c}>{c}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Type</Form.Label>
 <Form.Control
 name="type"
 value={form.type}
 onChange={handleChange}
 placeholder="e.g. Backhoe Loader"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label>Make / Brand</Form.Label>
 <Form.Control name="make" value={form.make} onChange={handleChange} placeholder="e.g. JCB" />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Model</Form.Label>
 <Form.Control name="model" value={form.model} onChange={handleChange} placeholder="e.g. 3CX Pro" />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Serial Number</Form.Label>
 <Form.Control name="serialNumber" value={form.serialNumber} onChange={handleChange} placeholder="Serial / Chassis No." />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Year of Manufacture</Form.Label>
 <Form.Control
 type="number"
 name="yearOfManufacture"
 value={form.yearOfManufacture}
 onChange={handleChange}
 placeholder="e.g. 2020"
 min={1900}
 max={new Date().getFullYear() + 1}
 />
 </Form.Group>
 </Col>
 </Row>

 <hr />

 {/* Assignment */}
 <h6 className="text-muted mb-3 text-uppercase" style={{ fontSize: "0.78rem", letterSpacing: 1 }}>
 Assignment
 </h6>
 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange}>
 {STATUSES.map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Project</Form.Label>
 <Form.Select name="project" value={form.project} onChange={handleChange}>
 <option value="">— Select Project —</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Work Order</Form.Label>
 <Form.Select name="workOrder" value={form.workOrder} onChange={handleChange}>
 <option value="">— Select Work Order —</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Operator</Form.Label>
 <Form.Select name="operator" value={form.operator} onChange={handleChange}>
 <option value="">— Select Operator —</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={8}>
 <Form.Group>
 <Form.Label>Location / Site</Form.Label>
 <Form.Control
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="Site address or description"
 />
 </Form.Group>
 </Col>
 </Row>

 <hr />

 {/* Maintenance */}
 <h6 className="text-muted mb-3 text-uppercase" style={{ fontSize: "0.78rem", letterSpacing: 1 }}>
 Maintenance
 </h6>
 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Maintenance Status</Form.Label>
 <Form.Select name="maintenanceStatus" value={form.maintenanceStatus} onChange={handleChange}>
 {MAINTENANCE_STATUSES.map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Last Maintenance Date</Form.Label>
 <Form.Control
 type="date"
 name="lastMaintenanceDate"
 value={form.lastMaintenanceDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Next Maintenance Date</Form.Label>
 <Form.Control
 type="date"
 name="nextMaintenanceDate"
 value={form.nextMaintenanceDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 </Row>

 <hr />

 {/* Remarks */}
 <Row className="mb-4">
 <Col>
 <Form.Group>
 <Form.Label>Remarks / Notes</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 placeholder="Any additional notes..."
 />
 </Form.Group>
 </Col>
 </Row>

 <div className="d-flex gap-2">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? (
 <><Spinner size="sm" className="me-2" />{isEdit ? "Updating..." : "Saving..."}</>
 ) : (
 isEdit ? "Update Equipment" : "Add Equipment"
 )}
 </Button>
 <Button variant="outline-secondary" onClick={() => navigate("../equipment")}>
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </>
 );
};

export default EquipmentForm;
